/**
 * The running timer, opened up.
 *
 * Reached by tapping the timer bar on any tab, or the running card on Time.
 * Toggl's habit worth copying: start first, sort out the details later — so
 * everything you might have skipped when you pressed ▶ (the project, the task,
 * the kind, even when you actually started) can be fixed here without stopping.
 */

import { useEffect } from "react";

import { KindPicker } from "@/components/KindPicker";
import { formatElapsed } from "@/context/TimerContext";
import { useRunningTimerEditor } from "@/hooks/useRunningTimerEditor";

import { PickerRow, Sheet, TaskPicker, field, tapGhost, tapPrimary } from "./index";

export function RunningSheet({ onClose }: { onClose: () => void }) {
  const ed = useRunningTimerEditor();
  const { timer } = ed;

  // Stopped somewhere else while open (the laptop, say): close, rather than
  // stay open in the provider and pop back up when the next timer starts.
  useEffect(() => { if (!timer.running) onClose(); }, [timer.running, onClose]);
  if (!timer.running) return null;

  const close = () => { ed.commit(); onClose(); };

  return (
    <Sheet
      title="Timer running"
      closeLabel="Done"
      onClose={close}
      footer={
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => { ed.stopAndLog(); onClose(); }} style={{ ...tapPrimary, flex: 1 }}>Stop &amp; log</button>
          <button onClick={() => { void timer.discard(); onClose(); }} style={{ ...tapGhost, flex: "none", width: 104, color: "var(--bad)" }}>Discard</button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 6 }}>
        <div className="mono" style={{ fontSize: 46, fontWeight: 500, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
          {formatElapsed(timer.seconds)}
        </div>

        <input
          value={ed.note}
          onChange={(e) => ed.setNote(e.target.value)}
          onBlur={ed.commitNote}
          placeholder="What are you working on?"
          aria-label="Description"
          style={field}
        />

        <div style={{ borderTop: "1px solid var(--tint2)", borderBottom: "1px solid var(--tint2)" }}>
          <PickerRow
            label="Project"
            value={timer.meta.projectId ?? ""}
            onChange={ed.setProject}
            options={[
              { value: "", label: "— No project" },
              ...ed.selectable.map((p) => ({ value: p.id, label: p.client ? `${p.name} · ${p.client}` : p.name })),
            ]}
            color={ed.project?.color}
            primary={ed.project?.name}
            secondary={ed.project?.client}
            placeholder="No project"
          />
        </div>

        <TaskPicker
          value={timer.meta.itemId ?? ""}
          onChange={ed.setTask}
          groups={ed.taskGroups}
          disabled={!timer.meta.projectId}
        />

        <label style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 15, color: "var(--ink2)" }}>
          <span style={{ flex: 1 }}>Started at</span>
          <input
            type="time"
            value={ed.startText}
            onChange={(e) => ed.setStartText(e.target.value)}
            onBlur={ed.commitStart}
            aria-label="Start time"
            style={{ ...field, width: 140, flex: "none", textAlign: "center" }}
          />
        </label>

        <KindPicker value={ed.kind} choices={ed.choices} onChange={ed.setKind} size="lg" fromProject={!timer.meta.kind} />
      </div>
    </Sheet>
  );
}
